/**
 * Pure geometry for the Arrange sub-tool (issue #115). Given the
 * bounding boxes of a set of selected objects, compute the per-object
 * translation that aligns / centres / distributes them. Align / centre
 * frame against the ANCHOR — the first-selected object (#872), which
 * stays put while the others move to its edges / centre lines; without
 * an anchor they fall back to the union (outermost) bounding box of the
 * whole selection. Distribute always works within the union, where the
 * extreme objects define the span.
 *
 * Knows nothing about Paper.js, the Project model, or the DOM — callers
 * gather bounds from rendered items, run these, and translate each
 * object by the returned delta. That keeps the math unit-testable in
 * isolation.
 *
 * Distribute has a special case (issue #383): if one selected object's
 * bounding box matches the whole selection's union, it's treated as an
 * encompassing container — it stays put and the remaining objects float
 * evenly inside it (equal margins to its edges), rather than the
 * container being spaced out as just another object.
 */

export type ArrangeOp =
  | "align-left"
  | "align-right"
  | "align-top"
  | "align-bottom"
  // Centre each object on the selection's centre line. "horizontal"
  // moves along x (objects line up on a shared vertical centre axis);
  // "vertical" moves along y.
  | "center-horizontal"
  | "center-vertical"
  // Even spacing within the container. "horizontal" spreads objects
  // left→right with equal gaps; "vertical" spreads them top→bottom.
  | "distribute-horizontal"
  | "distribute-vertical";

export interface ArrangeBounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface ArrangeItem {
  id: string;
  bounds: ArrangeBounds;
}

export interface ArrangeDelta {
  id: string;
  dx: number;
  dy: number;
}

/** Union (outermost) bounding box of every item — the container the
 *  operations frame against. Assumes at least one item. */
function unionBounds(items: ReadonlyArray<ArrangeItem>): ArrangeBounds {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const it of items) {
    minX = Math.min(minX, it.bounds.x);
    minY = Math.min(minY, it.bounds.y);
    maxX = Math.max(maxX, it.bounds.x + it.bounds.width);
    maxY = Math.max(maxY, it.bounds.y + it.bounds.height);
  }
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}

/** Floating-point slop for matching an item's bounds against the
 *  selection union — geometry comes from rendered paths, so an exact
 *  `===` would be brittle. */
const BOUNDS_EPS = 1e-6;

/** The single item that spans the whole selection along the axis being
 *  distributed — the "encompassing" container (issue #383). Only the
 *  distribution axis is measured (issue #592): a box that frames the
 *  objects left-to-right is still the horizontal container even if those
 *  objects overflow it vertically (so the union is taller than the box),
 *  and vice-versa. `null` when no item reaches both axis extremes, or
 *  when more than one does (ambiguous — fall back to plain distribution).
 *  `pos`/`size` pick the distribution axis (x/width or y/height). */
function findContainer(
  items: ReadonlyArray<ArrangeItem>,
  u: ArrangeBounds,
  pos: (b: ArrangeBounds) => number,
  size: (b: ArrangeBounds) => number,
): ArrangeItem | null {
  const uPos = pos(u);
  const uSize = size(u);
  const matches = items.filter(
    (it) =>
      Math.abs(pos(it.bounds) - uPos) < BOUNDS_EPS &&
      Math.abs(size(it.bounds) - uSize) < BOUNDS_EPS,
  );
  return matches.length === 1 ? matches[0]! : null;
}

/** Distribute `inner` items between the fixed edges `[lo, hi]` with
 *  EQUAL gaps everywhere — including the margins from each edge to the
 *  first / last box, so the objects float evenly inside the frame
 *  (N+1 equal gaps for N items; issue #383). Returns the new
 *  minimum-edge position per id; empty when there are no inner items. */
function distributeWithinBounds(
  inner: ReadonlyArray<ArrangeItem>,
  lo: number,
  hi: number,
  pos: (b: ArrangeBounds) => number,
  size: (b: ArrangeBounds) => number,
): Map<string, number> {
  const out = new Map<string, number>();
  if (inner.length === 0) return out;
  const sorted = [...inner].sort((a, b) => pos(a.bounds) - pos(b.bounds));
  let totalSize = 0;
  for (const it of sorted) totalSize += size(it.bounds);
  // One more gap than boxes — the two end margins plus the inter-box
  // gaps are all equal. Negative when the boxes overflow the frame,
  // which just overlaps them evenly (same convention as `distributeAxis`).
  const gap = (hi - lo - totalSize) / (sorted.length + 1);
  let cursor = lo + gap;
  for (const it of sorted) {
    out.set(it.id, cursor);
    cursor += size(it.bounds) + gap;
  }
  return out;
}

/** Even-gap distribution along one axis. Returns the new minimum-edge
 *  position (left/top) for each item, keyed by id; the two extreme
 *  items keep their place and the in-between ones spread so the gaps
 *  between consecutive boxes are equal. A no-op (empty map) for fewer
 *  than three items. `pos`/`size` pick the axis (x/width or y/height). */
function distributeAxis(
  items: ReadonlyArray<ArrangeItem>,
  pos: (b: ArrangeBounds) => number,
  size: (b: ArrangeBounds) => number,
): Map<string, number> {
  const out = new Map<string, number>();
  if (items.length < 3) return out;
  const sorted = [...items].sort((a, b) => pos(a.bounds) - pos(b.bounds));
  const start = pos(sorted[0]!.bounds);
  let end = -Infinity;
  let totalSize = 0;
  for (const it of sorted) {
    totalSize += size(it.bounds);
    end = Math.max(end, pos(it.bounds) + size(it.bounds));
  }
  const span = end - start;
  // Equal gap between consecutive boxes — can go negative when the
  // objects overlap (total size exceeds the span); that just compresses
  // them evenly, which is the expected behaviour.
  const gap = (span - totalSize) / (sorted.length - 1);
  let cursor = start;
  for (const it of sorted) {
    out.set(it.id, cursor);
    cursor += size(it.bounds) + gap;
  }
  return out;
}

/**
 * Compute the translation for every item to satisfy `op`. Align /
 * centre frame against `anchorId`'s bounds when given (#872) — the
 * first-selected object stays put and the others come to it — else
 * against the selection's union bounding box. Distribute always frames
 * against the union (the extremes / an encompassing container define
 * the span; an anchor has no meaning there). Items not moved by the
 * operation get a zero delta, so the result always has one entry per
 * input item in input order. Align / centre need at least two items;
 * distribute needs at least three (otherwise every delta is zero).
 */
export function computeArrangeDeltas(
  items: ReadonlyArray<ArrangeItem>,
  op: ArrangeOp,
  anchorId?: string | null,
): ArrangeDelta[] {
  if (items.length < 2) {
    return items.map((it) => ({ id: it.id, dx: 0, dy: 0 }));
  }
  const u = unionBounds(items);
  const anchor =
    anchorId != null ? items.find((it) => it.id === anchorId) : undefined;
  // Align / centre frame: the anchor when known, else the union.
  const frame = anchor ? anchor.bounds : u;
  const left = frame.x;
  const right = frame.x + frame.width;
  const top = frame.y;
  const bottom = frame.y + frame.height;
  const cx = frame.x + frame.width / 2;
  const cy = frame.y + frame.height / 2;
  // Distribute frame: always the union's extremes.
  const uLeft = u.x;
  const uRight = u.x + u.width;
  const uTop = u.y;
  const uBottom = u.y + u.height;

  switch (op) {
    case "align-left":
      return items.map((it) => ({ id: it.id, dx: left - it.bounds.x, dy: 0 }));
    case "align-right":
      return items.map((it) => ({
        id: it.id,
        dx: right - (it.bounds.x + it.bounds.width),
        dy: 0,
      }));
    case "align-top":
      return items.map((it) => ({ id: it.id, dx: 0, dy: top - it.bounds.y }));
    case "align-bottom":
      return items.map((it) => ({
        id: it.id,
        dx: 0,
        dy: bottom - (it.bounds.y + it.bounds.height),
      }));
    case "center-horizontal":
      return items.map((it) => ({
        id: it.id,
        dx: cx - (it.bounds.x + it.bounds.width / 2),
        dy: 0,
      }));
    case "center-vertical":
      return items.map((it) => ({
        id: it.id,
        dx: 0,
        dy: cy - (it.bounds.y + it.bounds.height / 2),
      }));
    case "distribute-horizontal": {
      // Issue #383: when one selected object encompasses the whole
      // selection, it's a container, not a thing to space out. Hold it
      // still and float the rest evenly between its left / right edges;
      // otherwise fall back to the plain extremes-fixed distribution.
      const container = findContainer(
        items,
        u,
        (b) => b.x,
        (b) => b.width,
      );
      const targets = container
        ? distributeWithinBounds(
            items.filter((it) => it.id !== container.id),
            uLeft,
            uRight,
            (b) => b.x,
            (b) => b.width,
          )
        : distributeAxis(
            items,
            (b) => b.x,
            (b) => b.width,
          );
      return items.map((it) => ({
        id: it.id,
        dx: (targets.get(it.id) ?? it.bounds.x) - it.bounds.x,
        dy: 0,
      }));
    }
    case "distribute-vertical": {
      const container = findContainer(
        items,
        u,
        (b) => b.y,
        (b) => b.height,
      );
      const targets = container
        ? distributeWithinBounds(
            items.filter((it) => it.id !== container.id),
            uTop,
            uBottom,
            (b) => b.y,
            (b) => b.height,
          )
        : distributeAxis(
            items,
            (b) => b.y,
            (b) => b.height,
          );
      return items.map((it) => ({
        id: it.id,
        dx: 0,
        dy: (targets.get(it.id) ?? it.bounds.y) - it.bounds.y,
      }));
    }
  }
}
